import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { SongServices } from "../../../core/services/SongServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { SongFields, SongListParams } from "./SongDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Songs.css";
import SongFilters from "./SongFilters";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  getLabelFor,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import SongSingleAction from "./SongSingleAction";
import SongBulkAction from "./SongBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  songTypeObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import SingerCell from "../../components/Elements/SingerCell";
import ActorCell from "../../components/Elements/ActorCell";
import { isEmpty } from "lodash";
import SongInfoCell from "../../components/Elements/SongInfoCell";
import MediaPlay from "../../components/Elements/MediaPlay";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import { useMediaContext } from "../../../context/MediaContext";
import MediaPlayer from "../../components/Elements/MediaPlayer";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function Songs() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SongFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const modalRef = useRef<ImageModal>(null);
  const [selectedSong, setSelectedSongData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    language: "",
    language_id: "",
    mood: "",
    mood_id: "",
    name: "",
    singer: "",
    singer_id: "",
    singer_gender: "",
    singer_combination: "",
    actor: "",
    actor_id: "",
    lyricist: "",
    lyricist_id: "",
    composer: "",
    composer_id: "",
    label: "",
    label_id: "",
    genre: "",
    genre_id: "",
    sub_genre: "",
    sub_genre_id: "",
    license: "",
    license_id: "",
    song_type: "",
    media_type: "",
    sourced_from: "",
    media_category: "",
    tempo: "",
    tempo_id: "",
    era: "",
    era_id: "",
    album: "",
    festive_season: "",
    festive_season_id: "",
    release_year: "",
    rating_chart: "",
    rating_chart_id: "",
    status: "",
    smart_search: "",
  };
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getSongs(params: SongListParams) {
    try {
      setLoading(true);
      const res = await SongServices.getSongs(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setShowCreate(UtilityServices.hasPermission("create-song"));
  }

  useEffect(() => {
    init();
    getSongs(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, []);

  async function deleteSong(song: any) {
    try {
      let res = await SongServices.deleteSong(song?.current?.id);
      setPage(1);
      getSongs(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishSong(song: any) {
    try {
      let status =
        song?.current?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;

      let res = await SongServices.publishSong(song?.current?.id, {
        status,
      });
      setPage(1);
      getSongs(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkPublishAction(status: string) {
    try {
      let res = await SongServices.publishBulkSong(checkedKeys, { status });
      setPage(1);
      getSongs(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      let res = await SongServices.deleteBulkSong(checkedKeys);
      setPage(1);
      getSongs(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportSongAction() {
    try {
      await SongServices.exportSongs(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  async function deletePic() {
    try {
      setOpenPictureDelModal(false);

      let res = await SongServices.deleteSongLogo(selectedSong.id);
      setPage(1);
      getSongs(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function songPicModal(value: any) {
    if (
      !failedImages.includes(value.media_image) &&
      value.media_image != null &&
      value.media_image !== ""
    ) {
      setSelectedSongData(value);
      modalRef.current?.openModal();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: SongListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getSongs(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getSongs(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getSongs(getSearchParams(formData));
  }

  function genreTitle(rData: any) {
    let gData = "";
    if (!isEmpty(rData.genre_title)) gData += rData.genre_title;
    if (!isEmpty(rData.sub_genre_title))
      gData += " (" + rData.sub_genre_title + ")";

    return gData;
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSongs(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterSongs = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getSongs(getSearchParams({ smart_search: smartSearchInput, rows: rows }));
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSongs(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getSongs(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const publishPermission = UtilityServices.hasPermission("publish-song");
  const updatePermission = UtilityServices.hasPermission("update-song");
  const deletePermission = UtilityServices.hasPermission("delete-song");
  const createUserPermission = UtilityServices.hasPermission("create-user");
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
    create_user: createUserPermission,
  };

  return (
    <div className="songs">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>
                {t("SONG")} {t("LIBRARY")}
              </h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button
                  appearance="primary"
                  onClick={() => navigate(ContentRoutes.CREATE_SONG)}
                >
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content className="songs">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <SongFilters onSubmit={submitSearch} onClear={clearSearch} />
              <div className="row song-controls-row">
                <SongBulkAction
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onBulkPublish={bulkPublishAction}
                  onBulkDelete={bulkDeleteAction}
                  onExportSong={exportSongAction}
                />
                <div className="col-md-4">
                  <div className="song-smart-search-wrap">
                    <InputGroup className="song-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("SONG").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("SINGER").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterSongs(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterSongs(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "song-table" : "song-empty-wrap"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div className="song-empty-state">
                  <div className="song-empty-icon">0</div>
                  <h4 className="song-empty-title">
                    {t("NO_ENTITY_FOUND", { entity: t("SONGS") })}
                  </h4>
                  <p className="song-empty-text">{t("ADJUST_FILTER")}</p>
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                />
              </Column>
              <Column width={200} sortable resizable fixed>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="title">
                  {(rowData: any) => (
                    <span title={`${rowData.title}`}>
                      {displayField(rowData.title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80} sortable resizable>
                <CustomHeaderCell>{t("LICENSE")}</CustomHeaderCell>
                <CustomCell dataKey="license_title">
                  {(rowData: any) => (
                    <span>{displayField(rowData.license_title)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160}>
                <CustomHeaderCell>{t("SINGER")}</CustomHeaderCell>
                <SingerCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("ALBUM")}</CustomHeaderCell>
                <CustomCell dataKey="album_name">
                  {(rowData: any) => (
                    <span title={`${rowData.album_name}`}>
                      {displayField(rowData.album_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160}>
                <CustomHeaderCell>{t("ACTOR")}</CustomHeaderCell>
                <ActorCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("GENRE")}</CustomHeaderCell>
                <CustomCell dataKey="genre_title">
                  {(rowData: any) => (
                    <span title={genreTitle(rowData)}>
                      {displayField(rowData.genre_title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="song_type">
                  {(rowData: any) => (
                    <span>
                      {getLabelFor(rowData.song_type, songTypeObjArr)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("LANGUAGE")}</CustomHeaderCell>
                <CustomCell dataKey="language_title">
                  {(rowData: any) => (
                    <span>{displayField(rowData.language_title)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("YEAR")}</CustomHeaderCell>
                <CustomCell dataKey="release_year">
                  {(rowData: any) => (
                    <span>{displayField(rowData.release_year)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("MORE")}</CustomHeaderCell>
                <SongInfoCell
                  rowData={(rowData: any) => rowData}
                  showData={[
                    "era",
                    "mood",
                    "lyricist",
                    "composer",
                    "tempo",
                    "media_type",
                    "sourced_from",
                    "rating_chart",
                  ]}
                />
              </Column>
              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <SongSingleAction
                      songData={rowData}
                      onDeleteSong={deleteSong}
                      onPublishSong={publishSong}
                      permission={permissionObj}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <MediaPlayer />
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("LOGO")}
        heading={t("LOGO")}
        body={
          <img
            src={selectedSong.media_image}
            className="main-profile-pic"
            alt={t("LOGO")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
      <AlertModal
        open={openPictureDelModal}
        handleClose={(e: any) => {
          if (e == true) {
            deletePic();
          } else {
            setOpenPictureDelModal(false);
            setSelectedSongData({});
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("SONG_LOGO").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
}

export default Songs;
